import { WarningIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { ErrorBoundary } from '../../../components/error-boundaries/error-boundary.tsx'
import {
  useAllIntegrations,
  useGetIntegrationsAllowedMethods,
  useIntegrationCount,
} from '../../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../../utils/text-utils.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import { InstanceCard, ReadOnlyInstanceCard } from './instance-card.tsx'
import type { CatalogEntry } from './integration-instance-utils.ts'
import {
  SCM_INTEGRATION_CATALOG,
  TOOL_INTEGRATION_CATALOG,
  getAllInstances,
  getIsNeedsAttention,
} from './integration-instance-utils.ts'
import * as styles from './integrations-tab.css.ts'
import { ManageDrawer } from './manage-drawer.tsx'
import { SetupDrawer } from './setup-drawer.tsx'
import { ToolCatalogCard } from './tool-catalog-card.tsx'

/**
 * How many integrations the tab badge reports.
 *
 * Every instance the tab lists, managed and system alike. A system instance is configured in the
 * Pixee Enterprise Server admin dashboard rather than here, but it is still an integration this
 * installation has, and the badge has to agree with the number of cards below it. The count reads
 * the `total` of a one-item page, so it costs one small request rather than every page of the
 * collection.
 *
 * Non-suspense so the badge renders while the request is in flight rather than suspending the page;
 * an unsettled count reads as 0, which is what the badge showed before it loaded anyway.
 *
 * This can still exceed the number of cards `IntegrationsTabContent` draws below. The badge is the
 * platform's `total`, while the cards come from `_embedded.items` after
 * `IntegrationPaginatedResponseSchema` has dropped every item whose `type` this build does not
 * model — so an org running a tool the platform gained ahead of the front end sees a badge of 5 over
 * three cards. `total` is kept because it is the honest count of what the org has configured, and
 * because the badge renders on every organization page: making it agree with the cards would mean
 * paging the whole collection to draw one number.
 */
export const useConfiguredIntegrationCount = (): number => {
  const { count } = useIntegrationCount()
  return count ?? 0
}

/**
 * Whether this installation opted into in-product integration management.
 *
 * The collection answers an OPTIONS probe with the methods it supports; an installation serving
 * integrations read-only omits POST and answers every write with 405. Composing that into a single
 * boolean belongs here rather than in `api-hooks`, which stays a thin wrapper over the endpoint.
 * A probe that errors is read as enabled: a backend that does not answer OPTIONS at all should not
 * cost the whole tab its affordances, and a write that turns out to be refused surfaces its own 405.
 */
const useIsManagementEnabled = (): { isEnabled: boolean; isPending: boolean } => {
  const { data: allowedMethods, isPending, isError } = useGetIntegrationsAllowedMethods()
  return { isEnabled: isError || (allowedMethods?.includes('POST') ?? false), isPending }
}

// Source providers first: a repository reaches Pixee through one, and a scanner has nothing to
// report on until it has. They were absent from this catalog while a deployment could have only one
// of each, connected at install time through the admin console.
const CATALOG_GROUPS = [
  { heading: 'Source Providers', entries: SCM_INTEGRATION_CATALOG },
  { heading: 'Scanner Integrations', entries: TOOL_INTEGRATION_CATALOG },
] as const

type ActiveDrawer =
  | { kind: 'setup'; entry: CatalogEntry }
  | { kind: 'docs'; entry: CatalogEntry }
  | { kind: 'manage'; integration: Integration }
  | undefined

export const IntegrationsTab: React.FC = () => {
  return (
    <ErrorBoundary fallbackTitle="Failed to load integrations">
      <IntegrationsTabContent />
    </ErrorBoundary>
  )
}

const IntegrationsTabContent: React.FC = () => {
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(undefined)

  const { integrations, isLoading: isLoadingIntegrations } = useAllIntegrations()

  // In-product management is opt-in per install. Where it is off the backend rejects writes, so the
  // tab collapses to a read-only list — no catalog, no create/edit/delete affordances. Wait for the
  // probe before deciding so the tab never flashes the read-only layout while it is still in flight.
  const { isEnabled: isManagementEnabled, isPending: isManagementProbePending } = useIsManagementEnabled()

  // Not memoised: `useAllIntegrations` rebuilds its array on every render, so any dependency on it
  // changes identity every time and a `useMemo` here would recompute anyway while reading as though
  // it did not. The sort is cheap against a list this page already renders in full.
  const instances = getAllInstances(integrations)

  const closeDrawer = () => setActiveDrawer(undefined)

  // The drawers are siblings of the body rather than children of it, so nothing the body switches
  // on can unmount them. `isLoadingIntegrations` is not first-load-only: the page walk inside
  // `useAllIntegrations` derives its page keys from the size each response reports, so a background
  // refetch — every return to this browser tab, under the global `staleTime: 0` — can raise it again
  // with the list already on screen. While a drawer rendered inside the body, that flash threw away
  // half-entered credentials and replayed the drawer's open animation, because `activeDrawer` lives
  // out here and brought it straight back empty.
  return (
    <>
      {match({ isPending: isManagementProbePending || isLoadingIntegrations, isManagementEnabled })
        .with({ isPending: true }, () => <div className={styles.loading}>Loading…</div>)
        .with({ isManagementEnabled: false }, () => <ReadOnlyIntegrationsList integrations={instances} />)
        .otherwise(() => (
          <ManagedIntegrationsList instances={instances} onOpenDrawer={setActiveDrawer} />
        ))}

      {activeDrawer?.kind === 'setup' && <SetupDrawer entry={activeDrawer.entry} onClose={closeDrawer} />}
      {activeDrawer?.kind === 'docs' && (
        <DocumentationDrawer
          entry={activeDrawer.entry}
          onClose={closeDrawer}
          onAddIntegration={() => setActiveDrawer({ kind: 'setup', entry: activeDrawer.entry })}
        />
      )}
      {activeDrawer?.kind === 'manage' && <ManageDrawer integration={activeDrawer.integration} onClose={closeDrawer} />}
    </>
  )
}

type ManagedIntegrationsListProps = {
  instances: Integration[]
  onOpenDrawer: (drawer: ActiveDrawer) => unknown
}

// What the tab shows where in-product management is on: the configured instances, then the catalog
// of what else can be connected.
const ManagedIntegrationsList: React.FC<ManagedIntegrationsListProps> = ({ instances, onOpenDrawer }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isAvailableHidden = searchParams.get('available') === 'hidden'

  const needsAttentionCount = instances.filter(getIsNeedsAttention).length

  const toggleAvailable = () =>
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous)
        if (isAvailableHidden) {
          next.delete('available')
        } else {
          next.set('available', 'hidden')
        }
        return next
      },
      { replace: true }
    )

  // Until the tab has something to list the catalog is the whole point of it, so it renders on its
  // own. Once anything is connected it moves below an "Available" division that can be hidden. A
  // system instance counts here even though the tab badge does not count it: the deployment plainly
  // is connected to something, so "No integrations" would be untrue.
  const isEmpty = instances.length === 0

  return (
    <div className={styles.container}>
      {needsAttentionCount > 0 && (
        <div className={styles.attentionBanner} role="status">
          <WarningIcon size={20} weight="light" className={styles.attentionBannerIcon} />
          <p className={styles.attentionBannerText}>
            <span className={styles.attentionBannerCount}>{needsAttentionCount}</span>{' '}
            {pluralize(needsAttentionCount, 'integration needs', 'integrations need')} your attention
          </p>
        </div>
      )}

      {isEmpty ? (
        <>
          <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
            <p className={styles.emptyStateTitle}>No integrations</p>
            <p className={styles.emptyStateSubtitle}>Connect a tool to get started</p>
          </div>
          <CatalogGrid entries={CATALOG_GROUPS.flatMap(group => group.entries)} onOpenDrawer={onOpenDrawer} />
        </>
      ) : (
        <>
          <div className={styles.cardGrid}>
            {instances.map(integration => (
              <InstanceCard
                key={integration.id}
                integration={integration}
                onManage={() => onOpenDrawer({ kind: 'manage', integration })}
              />
            ))}
          </div>

          <hr className={styles.divider} />

          <div className={styles.availableHeaderRow}>
            <h2 className={styles.availableHeading}>Available</h2>
            <DefaultButton
              buttonType={isAvailableHidden ? 'primary' : 'secondary'}
              size="small"
              onClick={toggleAvailable}
            >
              {isAvailableHidden ? 'Show available' : 'Hide available'}
            </DefaultButton>
          </div>

          {!isAvailableHidden && (
            <div className={styles.catalogGroups}>
              {CATALOG_GROUPS.map(group => (
                <section key={group.heading}>
                  <h3 className={styles.catalogGroupHeading}>{group.heading}</h3>
                  <CatalogGrid entries={group.entries} onOpenDrawer={onOpenDrawer} />
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

type CatalogGridProps = {
  entries: CatalogEntry[]
  onOpenDrawer: (drawer: ActiveDrawer) => unknown
}

const CatalogGrid: React.FC<CatalogGridProps> = ({ entries, onOpenDrawer }) => (
  <div className={styles.catalogGrid}>
    {entries.map(entry => (
      <ToolCatalogCard
        key={entry.id}
        entry={entry}
        onAdd={() => onOpenDrawer({ kind: 'setup', entry })}
        onViewDocs={() => onOpenDrawer({ kind: 'docs', entry })}
      />
    ))}
  </div>
)

// What the tab shows where in-product integration management is off: name and type only, nothing to
// act on. Lists the same instances the managed grid does, so the two views cannot disagree about
// what the deployment is connected to. On an installation that never opted in, that list is exactly
// the admin-console connections, which is what this view exists to show.
const ReadOnlyIntegrationsList: React.FC<{ integrations: Integration[] }> = ({ integrations }) => (
  <div className={styles.container}>
    {integrations.length === 0 ? (
      <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
        <p className={styles.emptyStateTitle}>No integrations</p>
      </div>
    ) : (
      <div className={styles.cardGrid}>
        {integrations.map(integration => (
          <ReadOnlyInstanceCard key={integration.id} integration={integration} />
        ))}
      </div>
    )}
  </div>
)
